import { useId, useState } from "react";
import {
  checkPassword,
  suggestPassword,
  type Identity,
} from "./password";

/**
 * ── The new-password pair, wherever a password is chosen ───────────────────
 *
 * Two screens set a password — accepting an invitation and finishing a reset
 * — and both must apply the same rules, or the stricter one is theatre. So
 * the pair of fields, the meter, the checklist and the generator live here
 * and are used twice rather than written twice.
 *
 * The checklist is the point. A rule the person only learns about by having
 * their submit rejected is a rule that reads as an obstacle; the same rule
 * ticking itself off as they type reads as progress, and they land on a
 * stronger password than the minimum without being argued with.
 *
 * Callers own the state and gate their own submit button with
 * `passwordsAcceptable` — this component reports, it does not decide.
 */
export function PasswordFields({
  label = "Choose a password",
  password,
  confirm,
  onPassword,
  onConfirm,
  identity = {},
  autoFocus = false,
}: {
  label?: string;
  password: string;
  confirm: string;
  onPassword: (value: string) => void;
  onConfirm: (value: string) => void;
  /** The invitee's own name and address — banned from their own password. */
  identity?: Identity;
  autoFocus?: boolean;
}) {
  const id = useId();
  const [reveal, setReveal] = useState(false);
  const [touched, setTouched] = useState(false);

  const verdict = checkPassword(password, identity);
  const started = password.length > 0 || touched;
  const mismatch = confirm.length > 0 && confirm !== password;

  function suggest() {
    const generated = suggestPassword(identity);
    onPassword(generated);
    onConfirm(generated);
    // Shown, not hidden: a suggestion nobody can read is a suggestion nobody
    // can write down or check against the manager that just offered to save it.
    setReveal(true);
    setTouched(true);
  }

  return (
    <>
      <div className="field">
        <div className="field-row">
          <label htmlFor={`${id}-pw`}>{label}</label>
          <button type="button" className="link-btn" onClick={suggest}>
            Suggest a strong one
          </button>
        </div>

        <div className="pw-input">
          <input
            id={`${id}-pw`}
            className="input"
            type={reveal ? "text" : "password"}
            value={password}
            onChange={(e) => onPassword(e.target.value)}
            onFocus={() => setTouched(true)}
            autoComplete="new-password"
            aria-describedby={`${id}-rules`}
            required
            autoFocus={autoFocus}
          />
          <button
            type="button"
            className="link-btn pw-reveal"
            onClick={() => setReveal((on) => !on)}
            aria-pressed={reveal}
          >
            {reveal ? "Hide" : "Show"}
          </button>
        </div>

        {started && (
          <>
            <div className="pw-meter-row">
              <div
                className="pw-meter"
                data-score={verdict.score}
                role="presentation"
              >
                <i />
                <i />
                <i />
                <i />
              </div>
              <span className="pw-meter-label" data-score={verdict.score}>
                {verdict.label}
              </span>
            </div>

            {/* One line, and only about what is still wrong — the checklist
                below already says everything that is right. */}
            <p className="field-hint" aria-live="polite">
              {verdict.advice ?? "Strong enough. Save it in your manager now."}
            </p>

            <ul className="pw-rules" id={`${id}-rules`}>
              {verdict.rules.map((rule) => (
                <li key={rule.id} className="pw-rule" data-met={rule.met}>
                  <span className="pw-tick" aria-hidden="true">
                    {rule.met ? "✓" : "○"}
                  </span>
                  <span>{rule.label}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="field">
        <label htmlFor={`${id}-confirm`}>Confirm password</label>
        <input
          id={`${id}-confirm`}
          className="input"
          type={reveal ? "text" : "password"}
          value={confirm}
          onChange={(e) => onConfirm(e.target.value)}
          autoComplete="new-password"
          aria-invalid={mismatch || undefined}
          required
        />
        {mismatch && (
          <span className="field-hint pw-mismatch">
            These two don't match yet.
          </span>
        )}
      </div>
    </>
  );
}
